import { useLazyQuery, useMutation } from '@apollo/client';
import { Button } from 'components/Button';
import { Loader } from 'components/Loader';
import { withNotification } from 'components/Notification';
import { Text } from 'components/Text';
import { useStore } from 'context/store-context';
import { CHANGE_PAYMENT_METHOD } from 'gql/mutations';
import { GET_PAYMENT_METHOD_INFO } from 'gql/queries';
import React, { useEffect, useState } from 'react';
import Cards, { Focused } from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import { NotificationTypes } from 'utils';
import { isMerchNowManagedBilling } from 'utils/storeBillingPolicy';

const cardBrands = new Map([['amex', 'American Express'], ['visa', 'Visa'], ['mastercard', 'Mastercard']]);

const PaymentMethod = ({ showNotification }: any) => {
  const { storeId, storeProperties } = useStore();
  const currentStore = storeId && storeProperties?.id === storeId ? storeProperties : undefined;
  const managedBilling = isMerchNowManagedBilling(currentStore);
  const [billingResult, setBillingResult] = useState<{ storeId: string; details: any; error?: boolean } | null>(null);
  const billingDetails = billingResult?.storeId === storeId ? billingResult?.details : null;
  const customApp = currentStore?.useCustomApp;
  const hasBilling = Boolean(currentStore?.billing);
  const customerId = currentStore?.billing?.customer?.id;
  const savedPaymentMethodId = currentStore?.billing?.payment_method?.id;
  const defaultPaymentMethod = currentStore?.billing?.customer?.invoice_settings?.default_payment_method;
  const defaultPaymentMethodId = typeof defaultPaymentMethod === 'string' ? defaultPaymentMethod : defaultPaymentMethod?.id;

  const [focused, setFocussed] = useState<Focused>('number');

  const onClickCard = () => {
    setFocussed((focused) => (focused === 'cvc' ? 'number' : 'cvc'));
  };

  const [getPaymentMethod, { loading: getPaymentMethodLoading }] = useLazyQuery(GET_PAYMENT_METHOD_INFO);

  const [changePaymentMethod] = useMutation(CHANGE_PAYMENT_METHOD, {
    onCompleted: (data) => {
      if (data && data.changeStripePaymentMethod) {
        showNotification(NotificationTypes.success, 'You will be redirected');
        window.open(data.changeStripePaymentMethod);
      } else {
        showNotification(
          NotificationTypes.error,
          'Unknown error occurred, retry again',
        );
      }
    },
  });

  useEffect(() => {
    let active = true;
    if (
      storeId &&
      customApp &&
      hasBilling
    ) {
      getPaymentMethod({
        variables: { storeId },
        fetchPolicy: 'network-only',
      }).then(({ data, error }) => {
        if (active) {
          setBillingResult({
            storeId,
            details: error ? null : data?.getStripePaymentMethodDetail ?? null,
            error: Boolean(error),
          });
        }
      }).catch(() => {
        if (active) setBillingResult({ storeId, details: null, error: true });
      });
    } else {
      setBillingResult(null);
    }
    return () => { active = false; };
  }, [getPaymentMethod, storeId, customApp, hasBilling, customerId, savedPaymentMethodId, defaultPaymentMethodId]);

  const onHandlePaymentMethod = () => {
    if (storeId && currentStore && !managedBilling) {
      changePaymentMethod({
        variables: {
          storeId,
        },
      });
    }
  };

  if (!currentStore) return <Loader />;

  if (managedBilling) {
    const card = currentStore.billing?.payment_method?.card;
    const last4 = typeof card?.last4 === 'string' && /^\d{4}$/.test(card.last4) ? card.last4 : null;
    const brand = cardBrands.get(card?.brand) || 'Card';
    return (
      <section className="rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold">Payment Method</h2>
        <p className="font-semibold">Stripe Billing</p>
        <p className="mt-2 text-sm text-gray-600">Billing is managed through your MerchNow account.</p>
        {last4 && <p className="mt-3 text-sm">{brand} •••• {last4}</p>}
      </section>
    );
  }

  if (getPaymentMethodLoading || (customApp && hasBilling && billingResult?.storeId !== storeId)) {
    return <Loader />;
  }

  if (customApp && hasBilling && billingResult?.storeId === storeId && billingResult?.error) {
    return (
      <section className="rounded-lg border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold">Payment Method</h2>
        <p role="alert">We couldn’t load your payment method. Refresh to try again.</p>
      </section>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-white shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Payment Method</h2>

      {currentStore.useCustomApp ? (
        <div>
          <div className="flex justify-between my-5 w-full">
            <div className="flex flex-col justify-between">
              <div>
                <div className="font-bold text-md">Stripe Billing</div>
                {billingDetails ? (
                  <div>
                    <Text
                      className="font-bold"
                      value={`Name: ${billingDetails.billing_details.name}`}
                    />
                    <Text
                      className="text-gray-400"
                      value={`Email: ${billingDetails.billing_details.email}`}
                    />
                  </div>
                ) : (
                  <div>No Payment Method Added</div>
                )}
              </div>
              <Button
                onClick={onHandlePaymentMethod}
                className="w-fit mt-5 text-center"
              >
                {billingDetails
                  ? 'Change Payment Method'
                  : 'Add Payment Method'}
              </Button>
            </div>
            {billingDetails && (
              <div onClick={onClickCard} className="cursor-pointer">
                <Cards
                  number={`**** **** **** ${billingDetails.card.last4}`}
                  name={billingDetails.billing_details.name}
                  expiry={`${String(billingDetails.card.exp_month).padStart(
                    2,
                    '0',
                  )}/${String(billingDetails.card.exp_year).slice(-2)}`}
                  cvc={''}
                  focused={focused}
                  preview
                  issuer={billingDetails.card.brand}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="font-bold text-md py-4">Shopify Billing</div>
        </div>
      )}
    </div>
  );
};

export default withNotification(PaymentMethod);
